/**
 * Theme v2 — page canvas.
 *
 * Light grey with a fine grain and a teal→indigo glow in the corner.
 * The document paper and sidebar panels float on top of it as white
 * cards.
 *
 * The prototype ships this as a 1.8 MB PNG; it's a gradient plus grain,
 * so it's rebuilt here in CSS instead.
 *
 * `background-attachment: fixed` measures the layers against the
 * viewport, not the element — that pins the glow to the screen's
 * bottom-right corner however far the content scrolls, and lets
 * neighbouring elements share one continuous background.
 */

/** Noise tile. `stitchTiles` makes the repeat seamless. */
const NOISE_TILE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.38'/%3E%3C/svg%3E\")";

/** Teal bloom, just below the bottom-right corner. */
const GLOW_TEAL =
  "radial-gradient(120% 90% at 82% 108%, rgba(94, 234, 212, 0.18) 0%, rgba(94, 234, 212, 0) 60%)";

/** Indigo edge, hugging the right border above the teal. */
const GLOW_INDIGO =
  "radial-gradient(70% 60% at 108% 88%, rgba(129, 140, 248, 0.22) 0%, rgba(129, 140, 248, 0) 62%)";

/**
 * Flat base the canvas layers sit on. Exported separately so consumers
 * that need to reason about the canvas colour — contrast checks, an
 * element that has to match it — read the same value `canvas` paints.
 */
const CANVAS_BASE = "#F1F1F1";

export const backgrounds = {
  canvasBase: CANVAS_BASE,
  /** Interpolate into a styled-component: ``styled.div`${backgrounds.canvas}` `` */
  canvas: `
    background-color: ${CANVAS_BASE};
    background-image: ${NOISE_TILE}, ${GLOW_TEAL}, ${GLOW_INDIGO};
    background-repeat: repeat, no-repeat, no-repeat;
    background-position: 0 0, right bottom, right bottom;
    background-size: 140px 140px, cover, cover;
    background-attachment: fixed, fixed, fixed;
  `,
} as const;

export type Backgrounds = typeof backgrounds;
